import { useEffect, useState } from 'react';
import { Loader2, Signpost } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { useCommunityAvenues } from '@/hooks/use-community-avenues';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { AdminMember } from '@/types';

interface MemberAvenuesDialogProps {
  communityId: string;
  member: AdminMember | null;
  onClose: () => void;
}

export function MemberAvenuesDialog({ communityId, member, onClose }: MemberAvenuesDialogProps) {
  const { avenues, isLoading: isLoadingAvenues } = useCommunityAvenues(communityId);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoadingMember, setIsLoadingMember] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!member) {
      setSelectedIds([]);
      return;
    }

    let cancelled = false;
    setIsLoadingMember(true);

    axiosInstance
      .get(`/api/communities/${communityId}/members/${member.id}/avenues`)
      .then((res) => {
        if (!cancelled) {
          setSelectedIds(res.data.avenueIds ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load member avenues');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMember(false);
      });

    return () => {
      cancelled = true;
    };
  }, [communityId, member]);

  const toggleAvenue = (avenueId: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, avenueId] : current.filter((id) => id !== avenueId)
    );
  };

  const handleSave = async () => {
    if (!member) return;
    setIsSaving(true);
    try {
      await axiosInstance.put(`/api/communities/${communityId}/members/${member.id}/avenues`, {
        avenueIds: selectedIds,
      });
      toast.success('Avenue assignments updated');
      onClose();
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to update avenues';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const activeAvenues = avenues.filter((avenue) => avenue.isActive);

  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Signpost className="h-5 w-5 text-primary" />
            Assign Avenues
          </DialogTitle>
          <DialogDescription>
            {member
              ? `Choose which district avenues ${member.name} belongs to. Members with avenues create district projects.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {isLoadingAvenues || isLoadingMember ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : activeAvenues.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No active avenues defined. Add avenues in Community Settings first.
          </p>
        ) : (
          <div className="space-y-3 py-2 max-h-64 overflow-y-auto">
            {activeAvenues.map((avenue) => (
              <div key={avenue.id} className="flex items-center gap-3 rounded-lg border px-4 py-3">
                <Checkbox
                  id={`avenue-${avenue.id}`}
                  checked={selectedIds.includes(avenue.id)}
                  onCheckedChange={(checked) => toggleAvenue(avenue.id, checked === true)}
                />
                <Label
                  htmlFor={`avenue-${avenue.id}`}
                  className="flex-1 cursor-pointer font-medium"
                >
                  {avenue.name}
                </Label>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || isLoadingAvenues || isLoadingMember || !member}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
